import { createContext, useContext, useEffect } from "react";

type UseTranslationModuleOptions = {
  waitForReady?: boolean;
};

type ModuleTranslationsContextValue<ModuleName extends string> = {
  ensureModuleLoaded: (moduleName: ModuleName) => Promise<void>;
  isModuleLoaded: (moduleName: ModuleName) => boolean;
};

export function createModuleTranslationsRuntime<ModuleName extends string>(): {
  ModuleTranslationsContext: React.Context<ModuleTranslationsContextValue<ModuleName> | undefined>;
  useTranslationModule: (moduleName: ModuleName, options?: UseTranslationModuleOptions) => boolean;
} {
  const ModuleTranslationsContext = createContext<ModuleTranslationsContextValue<ModuleName> | undefined>(undefined);

  function useTranslationModule(moduleName: ModuleName, options?: UseTranslationModuleOptions): boolean {
    const context = useContext(ModuleTranslationsContext);
    const isLoaded = context?.isModuleLoaded(moduleName) ?? false;

    useEffect(() => {
      if (!context) return;
      void context.ensureModuleLoaded(moduleName);
    }, [context, moduleName]);

    return options?.waitForReady ? isLoaded : true;
  }

  return {
    ModuleTranslationsContext,
    useTranslationModule,
  };
}
